import { Stack, Text, Paper, Flex, Box, Tooltip } from "@mantine/core";
import { IconArrowNarrowRight, IconList } from "@tabler/icons-react";

// Colores por etapa del funnel
const STAGE_COLORS = {
    NEW: "#4dabf7",
    CONTACTED: "#748ffc",
    IN_PROGRESS: "#f59f00",
    WAITING_PAYMENT: "#ff922b",
    CONVERTED: "#51cf66",
    WARNING: "#ffd467",
    LOST: "#ff6b6b",
};

/**
 * Funnel visual con barras horizontales escalonadas.
 * Acepta onStageClick(stage) para abrir el listado de leads de esa etapa.
 */
export default function FunnelChart({ funnel, onStageClick }) {
    if (!funnel || funnel.length === 0) return null;

    const activeFunnel = funnel.filter((s) => s.stage !== "LOST");
    const lostStage = funnel.find((s) => s.stage === "LOST");
    const maxCount = Math.max(...activeFunnel.map((s) => s.count), 1);
    const isClickable = typeof onStageClick === "function";

    const renderBar = (stage, barWidth, isLost = false) => (
        <Box style={{ flex: 1, position: "relative" }}>
            <Tooltip
                label={isClickable ? `Ver ${stage.count} leads en "${stage.label}"` : stage.label}
                withArrow
                disabled={stage.count === 0}
            >
                <Box
                    onClick={() => isClickable && stage.count > 0 && onStageClick(stage.stage)}
                    style={{
                        width: `${barWidth}%`,
                        height: 36,
                        backgroundColor: STAGE_COLORS[stage.stage] || "#868e96",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: 12,
                        paddingRight: 12,
                        transition: "width 0.4s ease, opacity 0.2s ease",
                        minWidth: 60,
                        opacity: isLost ? 0.7 : 1,
                        cursor: isClickable && stage.count > 0 ? "pointer" : "default",
                    }}
                    onMouseEnter={(e) => {
                        if (isClickable && stage.count > 0) e.currentTarget.style.opacity = "0.85";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = isLost ? "0.7" : "1";
                    }}
                >
                    <Flex align="center" gap={6}>
                        <Text size="sm" fw={600} c="white">
                            {stage.count}
                        </Text>
                        {isClickable && stage.count > 0 && (
                            <IconList size={13} color="white" opacity={0.8} />
                        )}
                    </Flex>
                </Box>
            </Tooltip>
        </Box>
    );

    return (
        <Paper shadow="sm" radius="md" p="lg">
            <Flex justify="space-between" align="center" mb="md">
                <Text fw={600} size="lg">
                    Embudo de ventas
                </Text>
                {isClickable && (
                    <Text size="xs" c="dimmed">
                        Hacé click en una barra para ver los leads
                    </Text>
                )}
            </Flex>

            <Stack gap="xs">
                {activeFunnel.map((stage, idx) => {
                    const prevCount = idx > 0 ? activeFunnel[idx - 1].count : null;
                    const stepConversion =
                        prevCount && prevCount > 0
                            ? Math.round((stage.count / prevCount) * 1000) / 10
                            : null;

                    const barWidth = maxCount > 0
                        ? Math.max((stage.count / maxCount) * 100, 8)
                        : 8;

                    return (
                        <div key={stage.stage}>
                            {stepConversion !== null && (
                                <Flex align="center" gap={4} mb={4} ml={8}>
                                    <IconArrowNarrowRight size={14} color="#868e96" />
                                    <Text size="xs" c="dimmed">
                                        {stepConversion}% de conversión
                                    </Text>
                                </Flex>
                            )}

                            <Flex align="center" gap="sm">
                                <Text size="xs" fw={500} w={120} ta="right" style={{ flexShrink: 0 }}>
                                    {stage.label}
                                </Text>
                                {renderBar(stage, barWidth)}
                                <Text size="xs" c="dimmed" w={50} ta="right" style={{ flexShrink: 0 }}>
                                    {stage.percentage}%
                                </Text>
                            </Flex>
                        </div>
                    );
                })}

                {lostStage && lostStage.count > 0 && (
                    <Flex
                        align="center"
                        gap="sm"
                        mt="sm"
                        pt="sm"
                        style={{ borderTop: "1px dashed #dee2e6" }}
                    >
                        <Text size="xs" fw={500} w={120} ta="right" c="red" style={{ flexShrink: 0 }}>
                            {lostStage.label}
                        </Text>
                        {renderBar(lostStage, Math.max((lostStage.count / maxCount) * 100, 8), true)}
                        <Text size="xs" c="dimmed" w={50} ta="right" style={{ flexShrink: 0 }}>
                            {lostStage.percentage}%
                        </Text>
                    </Flex>
                )}
            </Stack>
        </Paper>
    );
}
